import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cn from 'classnames';
import { format } from 'date-fns';
import emailMask from 'text-mask-addons/dist/emailMask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import Validator from 'components/Validator/Validator';
import PageContent from 'components/Template/PageContent';
import Title from 'components/Title';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { Grid, Column } from 'components/Layout';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/FormGroupLabel';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Checkbox from 'components/Form/Checkbox';
import { Button } from 'components/Button';

import { applyApplication } from 'actions/borrowerApply';

import schema from './schema';
import style from './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
  integerLimit: 5,
});

const unmask = val => val.replace(/[$, ]+/g, '');

const BorrowerApply = (props) => {
  const { className, validator: { values, errors } } = props;
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const getCurrentDate = () => format(new Date(), 'MM/dd/yyyy');

  const handleSubmitFrom = (data, e) => {
    e.preventDefault();
    const { validator: { validate } } = props;
    if (validate(schema).isValid) {
      const formData = {
        ...values,
        requestedAmount: unmask(values.requestedAmount),
        serviceAmount: unmask(values.serviceAmount),
        serviceDate: getCurrentDate(),
      };

      dispatch(applyApplication({
        url: '/workflows/application/workflow/text2apply/start',
        data: formData,
        success: () => {
          setIsSubmitted(true);
        },
        fail: () => {},
      }));
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleCheckboxChange = (name, value) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(name, value);
  };

  return (
    <form className={cn(style.App, className)} onSubmit={handleSubmitFrom.bind(null, values)}>
      <Header />
      <section className="grid-container fluid borrowers-apply section">
        <div className="grid-container">
          <Grid className="grid-margin-x max-limited">
            {
            isSubmitted ?
              <PageContent>
                <Title>You have successfully sent text2apply link to applicant!</Title>
              </PageContent>
            :
              <Fragment>
                <Column className="small-12 form-headline">
                  <h2>Text to Apply Application</h2>
                </Column>

                <Column className="small-12 large-12 main-column">
                  <FormGroup className="form-group">
                    <FormGroupLabel value="Product/Service Information" />
                    <Grid className="grid-margin-x">
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="Total Amount Service"
                          name="serviceAmount"
                          value={`${values.serviceAmount}`}
                          isMasked={currencyMask}
                          onChange={handleInputChange}
                          hasError={!!errors.serviceAmount}
                          isRequired
                          notification="Please enter the cost of services"
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="Total Amount of Requested"
                          name="requestedAmount"
                          value={`${values.requestedAmount}`}
                          isMasked={currencyMask}
                          onChange={handleInputChange}
                          hasError={!!errors.requestedAmount}
                          isRequired
                          notification="Please enter the loan amount requested by the applicant"
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          name="serviceDate"
                          isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                          label="Date Of Service"
                          placeHolder="__/__/____"
                          value={getCurrentDate()}
                          onChange={handleInputChange}
                          hasError={!!errors.serviceDate}
                          tooltip="Please enter the date of service"
                          isDisabled
                        />
                      </Column>
                    </Grid>
                  </FormGroup>

                  <FormGroup>
                    <FormGroupLabel value="Applicant Personal Information" />
                    <Grid className="grid-margin-x">
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="First Name"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleInputChange}
                          isRequired
                          hasError={!!errors.firstName}
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="Last Name"
                          name="lastName"
                          value={values.lastName}
                          onChange={handleInputChange}
                          isRequired
                          hasError={!!errors.lastName}
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Select
                          name="stateOfResidence"
                          data={[
                            { value: 'alabama', title: 'Alabama' },
                            { value: 'alaska', title: 'Alaska' },
                            { value: 'arizona', title: 'Arizona' },
                            { value: 'arkansas', title: 'Arkansas' },
                            { value: 'california', title: 'California' },
                            { value: 'colorado', title: 'Colorado' },
                            { value: 'connecticut', title: 'Connecticut' },
                            { value: 'delaware', title: 'Delaware' },
                            { value: 'district-of-columbia', title: 'District of Columbia' },
                            { value: 'florida', title: 'Florida' },
                            { value: 'georgia', title: 'Georgia' },
                            { value: 'hawaii', title: 'Hawaii' },
                            { value: 'idaho', title: 'Idaho' },
                            { value: 'illinois', title: 'Illinois' },
                            { value: 'indiana', title: 'Indiana' },
                            { value: 'iowa', title: 'Iowa' },
                            { value: 'kansas', title: 'Kansas' },
                            { value: 'kentucky', title: 'Kentucky' },
                            { value: 'louisiana', title: 'Louisiana' },
                            { value: 'maine', title: 'Maine' },
                            { value: 'maryland', title: 'Maryland' },
                            { value: 'massachusetts', title: 'Massachusetts' },
                            { value: 'michigan', title: 'Michigan' },
                            { value: 'minnesota', title: 'Minnesota' },
                            { value: 'mississippi', title: 'Mississippi' },
                            { value: 'missouri', title: 'Missouri' },
                            { value: 'montana', title: 'Montana' },
                            { value: 'nebraska', title: 'Nebraska' },
                            { value: 'nevada', title: 'Nevada' },
                            { value: 'new-hampshire', title: 'New Hampshire' },
                            { value: 'new-jersey', title: 'New Jersey' },
                            { value: 'new-mexico', title: 'New Mexico' },
                            { value: 'new-york', title: 'New York' },
                            { value: 'north-carolina', title: 'North Carolina' },
                            { value: 'north-dakota', title: 'North Dakota' },
                            { value: 'ohio', title: 'Ohio' },
                            { value: 'oklahoma', title: 'Oklahoma' },
                            { value: 'oregon', title: 'Oregon' },
                            { value: 'pennsylvania', title: 'Pennsylvania' },
                            { value: 'rhode-island', title: 'Rhode Island' },
                            { value: 'south-carolina', title: 'South Carolina' },
                            { value: 'south-dakota', title: 'South Dakota' },
                            { value: 'tennessee', title: 'Tennessee' },
                            { value: 'texas', title: 'Texas' },
                            { value: 'utah', title: 'Utah' },
                            { value: 'vermont', title: 'Vermont' },
                            { value: 'virginia', title: 'Virginia' },
                            { value: 'washington', title: 'Washington' },
                            { value: 'west-virginia', title: 'West Virginia' },
                            { value: 'wisconsin', title: 'Wisconsin' },
                            { value: 'wyoming', title: 'Wyoming' },
                          ]}
                          value={values.stateOfResidence}
                          onChange={handleInputChange}
                          label="State of Residence"
                          isRequired
                          hasError={!!errors.stateOfResidence}
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="Email Address"
                          name="email"
                          isMasked={emailMask}
                          placeHolder="Email address"
                          value={values.email}
                          onChange={handleInputChange}
                          isRequired
                          hasError={!!errors.email}
                        />
                      </Column>
                      <Column className="small-12 medium-6 large-4">
                        <Input
                          label="Mobile Phone Number"
                          name="phoneNumber"
                          placeHolder="(___) ___-____"
                          isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                          value={values.phoneNumber}
                          onChange={handleInputChange}
                          isRequired
                          hasError={!!errors.phoneNumber}
                        />
                      </Column>
                      <Column className="small-12 checkbox-row padded-bottom">
                        <Checkbox
                          label={['I hereby certify that the applicant provided permission to send this application via text message']}
                          name="hasApplicationConsented"
                          onChange={handleCheckboxChange.bind(null, 'hasApplicationConsented')}
                          isChecked={values.hasApplicationConsented}
                          id="hasApplicationConsented"
                          errorMessage={errors.hasApplicationConsented}
                        />
                      </Column>
                    </Grid>
                    <Grid className="grid-margin-x">
                      <Column className="small-12 form-action-row">
                        <Grid className="grid-margin-x">
                          <Column className="small-12 large-4 large-offset-4">
                            <Button
                              className={cn('button large arrow green w-100', style.button)}
                              onClick={handleSubmitFrom.bind(null, values)}
                            >
                              Send Now
                            </Button>
                          </Column>
                        </Grid>
                      </Column>
                    </Grid>
                  </FormGroup>
                </Column>
              </Fragment>
            }

          </Grid>
        </div>
      </section>
      <Footer />
    </form>
  );
};

BorrowerApply.propTypes = {
  className: PropTypes.string,
  validator: PropTypes.object.isRequired,
};

BorrowerApply.defaultProps = {
  className: '',
};

export default compose(
  withRouter,
  Validator(schema)
)(BorrowerApply);




// WEBPACK FOOTER //
// ./src/containers/pages/TextApplyFrontend/index.js